import React, { useState } from 'react';
import { introCardKeyline } from '../designSystem';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const TXT = 'rgb(17, 17, 17)';
const MUTED = 'rgb(122, 107, 99)';
const PKD = 'rgb(153, 53, 86)';
const PINK_BG = 'rgb(251, 234, 240)';
const CATEGORY_CARD_BG = 'rgb(255, 252, 249)';
const GUIDE_CARD_BG = 'rgb(249, 246, 241)';
const PAGE_BG = 'rgb(249, 246, 241)';

const GUIDE_BUCKETS = [
  {
    id: 'treatment',
    icon: '⚠️',
    iconBg: 'rgb(251, 234, 240)',
    title: 'When treatment goes wrong',
    sublabel: 'OHSS · failed cycles · cancelled cycles · no embryos',
    guides: [
      {
        title: 'OHSS — What It Is and What to Watch For',
        desc: 'Warning signs, when to call your clinic, what to expect if symptoms escalate after retrieval.',
        time: '⏱ 8 min',
        novaBadge: '✦ Relevant to you · Stim days 1–8',
      },
      {
        title: 'After a Negative Beta — What Happens Next',
        desc: 'The immediate physical and emotional reality, what questions to ask, and what to do with your grief.',
        time: '⏱ 10 min',
      },
      {
        title: 'Cancelled Cycle',
        desc: 'What causes a cycle to be cancelled mid-stims, what it means for your protocol, and how to decide what\'s next.',
        time: '⏱ 7 min',
      },
      {
        title: 'No Embryos to Transfer',
        desc: 'One of the most devastating outcomes — what it means medically, emotionally, and where to go from here.',
        time: '⏱ 9 min',
      },
    ],
  },
  {
    id: 'loss',
    icon: '💔',
    iconBg: 'rgb(254, 226, 226)',
    title: 'When loss happens',
    sublabel: 'Miscarriage · RPL · chemical pregnancy · ectopic · second trimester loss',
    guides: [
      {
        title: 'Recurrent Pregnancy Loss',
        desc: 'Testing protocols, causes that are and aren\'t knowable, how to advocate for yourself when the answer is unexplained.',
        time: '⏱ 12 min',
      },
      {
        title: 'Chemical Pregnancy',
        desc: 'Why it happens, why it counts, and what to expect physically when the medical system often minimizes it.',
        time: '⏱ 7 min',
      },
      {
        title: 'Ectopic Pregnancy',
        desc: 'What it is, how it\'s treated, the physical and emotional recovery, and how it affects future fertility.',
        time: '⏱ 9 min',
      },
      {
        title: 'Second Trimester Loss',
        desc: 'The medical process, where to find support, and navigating TFMR when it\'s part of the story.',
        time: '⏱ 11 min',
      },
    ],
  },
  {
    id: 'costs',
    icon: '💰',
    iconBg: 'rgb(255, 241, 221)',
    title: 'When costs become a crisis',
    sublabel: 'IVF costs · insurance appeals · financing · medications',
    guides: [
      {
        title: 'IVF Costs — What You\'re Actually Paying For',
        desc: 'Full breakdown of what\'s included and what isn\'t — medications, monitoring, PGT-A, and the hidden fees.',
        time: '⏱ 9 min',
      },
      {
        title: 'When Insurance Denies Your Claim',
        desc: 'How to appeal, what documentation you need, how to escalate, and what your employer benefits may cover.',
        time: '⏱ 8 min',
      },
      {
        title: 'Financing IVF',
        desc: 'Loans, grants, shared risk programs — what the fine print actually means and how to evaluate your options.',
        time: '⏱ 8 min',
      },
      {
        title: 'Medication Costs — What to Expect',
        desc: 'Stim meds, trigger shots, progesterone — what they cost, pharmacy options, and how to reduce out-of-pocket spend.',
        time: '⏱ 7 min',
      },
    ],
  },
  {
    id: 'diagnosis',
    icon: '🧬',
    iconBg: 'rgb(225, 245, 238)',
    title: 'When the diagnosis changes everything',
    sublabel: 'DOR · male factor · unexplained infertility · endometriosis',
    guides: [
      {
        title: 'Diminished Ovarian Reserve',
        desc: 'What AMH and AFC actually tell you, what they don\'t, and how it affects your protocol and options going forward.',
        time: '⏱ 10 min',
      },
      {
        title: 'Male Factor Infertility',
        desc: 'What the SA report means, ICSI, surgical options, and the emotional weight of this diagnosis as a couple.',
        time: '⏱ 9 min',
      },
      {
        title: 'Unexplained Infertility',
        desc: 'Why this diagnosis is both common and maddening — and how to move forward without a clear answer.',
        time: '⏱ 8 min',
      },
      {
        title: 'Endometriosis and Fertility',
        desc: 'The connection, how it affects IVF outcomes, and what surgical and medical treatment options exist.',
        time: '⏱ 9 min',
      },
    ],
  },
  {
    id: 'emotional',
    icon: '💜',
    iconBg: 'rgb(238, 237, 254)',
    title: 'When the emotional weight becomes the story',
    sublabel: 'Postpartum · pregnancy after loss · when to stop · identity',
    guides: [
      {
        title: 'Postpartum After Infertility',
        desc: 'Why it can feel unexpectedly hard — PPMD, the identity shift, and when to ask for help.',
        time: '⏱ 10 min',
      },
      {
        title: 'Pregnancy After Loss',
        desc: 'The anxiety of pregnancy after miscarriage or RPL — what\'s normal to feel and how to manage appointments that feel terrifying.',
        time: '⏱ 8 min',
      },
      {
        title: 'When to Stop Treatment',
        desc: 'One of the hardest decisions in fertility — how to know when you\'ve reached your limit and what moving on actually looks like.',
        time: '⏱ 11 min',
      },
      {
        title: 'The Identity Shift',
        desc: 'Who you are after infertility — the grief, the anger, the love, and how to find yourself again on the other side of the journey.',
        time: '⏱ 9 min',
      },
    ],
  },
];

function GuideCard({ guide, navigate }) {
  return (
    <div
      style={{
        background: GUIDE_CARD_BG,
        border: '1px solid rgba(212, 83, 126, 0.15)',
        borderRadius: 10,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      {guide.novaBadge ? (
        <span
          style={{
            alignSelf: 'flex-start',
            background: PINK_BG,
            color: PKD,
            borderRadius: 999,
            padding: '2px 8px',
            fontSize: 9,
            fontWeight: 700,
          }}
        >
          {guide.novaBadge}
        </span>
      ) : null}
      <div style={{ fontSize: 12, fontWeight: 600, color: TXT, lineHeight: 1.35 }}>{guide.title}</div>
      <div style={{ fontSize: 10.5, color: MUTED, lineHeight: 1.5 }}>{guide.desc}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginTop: 'auto' }}>
        <span style={{ fontSize: 10, color: MUTED }}>{guide.time}</span>
        <button
          type="button"
          onClick={() => navigate?.('/signup?source=demo-fertility-guides')}
          style={{
            fontFamily: FONT_BODY,
            fontSize: 10,
            fontWeight: 600,
            color: PKD,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          Read →
        </button>
      </div>
    </div>
  );
}

export default function FertilityDemoGuides({ navigate, omitHeader = false }) {
  const [openBucketId, setOpenBucketId] = useState('treatment');

  const toggleBucket = (bucketId) => {
    setOpenBucketId((prev) => (prev === bucketId ? '' : bucketId));
  };

  return (
    <div
      style={{
        fontFamily: FONT_BODY,
        background: PAGE_BG,
        padding: '20px 8px',
        boxSizing: 'border-box',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {!omitHeader && (
        <div
          style={{
            background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))',
            ...introCardKeyline,
            border: '1px solid rgb(209, 142, 155)',
            borderRadius: 14,
            padding: '22px 26px',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div className="page-eyebrow">
              <span aria-hidden>🌸</span>
              <span>Fertility · Guides</span>
            </div>
            <button
              type="button"
              onClick={() => navigate?.('/signup?source=demo-fertility-guides')}
              style={{
                background: 'white',
                color: PKD,
                border: 'none',
                borderRadius: 999,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: FONT_BODY,
              }}
            >
              Join the waitlist
            </button>
          </div>
          <h1 className="page-title">Guides</h1>
          <p className="page-descriptor">
            Clinically grounded guides for the moments most apps don&apos;t prepare you for.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {GUIDE_BUCKETS.map((bucket) => (
          <div
            key={bucket.id}
            style={{
              background: CATEGORY_CARD_BG,
              border: '1px solid rgba(212, 83, 126, 0.2)',
              borderRadius: 12,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              onClick={() => toggleBucket(bucket.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '14px 16px',
                cursor: 'pointer',
                border: 'none',
                background: openBucketId === bucket.id ? 'rgba(251, 234, 240, 0.5)' : 'transparent',
                textAlign: 'left',
                fontFamily: FONT_BODY,
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: bucket.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {bucket.icon}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: TXT }}>{bucket.title}</div>
                <div style={{ fontSize: 10, color: MUTED, lineHeight: 1.35, marginTop: 1 }}>{bucket.sublabel}</div>
              </div>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: PKD,
                  background: PINK_BG,
                  borderRadius: 999,
                  padding: '1px 8px',
                  marginLeft: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                {bucket.guides.length} guides
              </span>
              <span style={{ fontSize: 11, color: MUTED, marginLeft: 8 }} aria-hidden>
                {openBucketId === bucket.id ? '▲' : '▼'}
              </span>
            </button>
            {openBucketId === bucket.id ? (
              <div style={{ padding: '0 14px 14px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  {bucket.guides.map((g) => (
                    <GuideCard key={g.title} guide={g} navigate={navigate} />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
